<script setup lang="ts">
import { onMounted, ref, computed, watch, onUnmounted } from "vue";
import {
  SearchMd,
  ChevronDown,
  ReverseLeft,
  Trash01,
  Eye,
} from "utruckin-icons";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import CommonStatus from "@/components/common/CommonStatus.vue";
import { useTaskStore } from "@/features/tasks/task.store";
import { useTeamStore } from "@/features/teams/team.store";
import { useWorkTypeStore } from "@/features/work-types/work-type.store";
import { ITaskData } from "@/features/tasks/task.types";
import { ITeam } from "@/features/teams/team.types";
import { IWorkType } from "@/features/work-types/work-type.types";
import { TaskStatus } from "@/features/tasks/task.enum";
import { DateTime } from "luxon";
import { useCompanyStore } from "@/features/company/company.store";
import { ICompany } from "@/features/company/company.types";
import { useUserStore } from "@/features/users/user.store";
import { useDriverStore } from "@/features/drivers/drivers.store";
import { useNotification } from "@/utils/notification";
import { useAppStore } from "@/store/app.store";
import { useArchiveStore } from "@/features/archive/archive.store";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import { IUser } from "@/features/users/user.types";
import CustomSearchDropdown from "@/components/custom/CustomSearchDropdown.vue";

const taskStore = useTaskStore();
const teamStore = useTeamStore();
const workTypeStore = useWorkTypeStore();
const companyStore = useCompanyStore();
const usersStore = useUserStore();
const driverStore = useDriverStore();
const appStore = useAppStore();
const archiveStore = useArchiveStore();
const { pushNotification } = useNotification();

const tasks = ref<ITaskData[]>([]);
const teams = ref<ITeam[]>([]);
const companies = ref<ICompany[]>([]);
const workTypes = ref<IWorkType[]>([]);
const problems = ref<IWorkType[]>([]);
const users = ref<IUser[]>([]);

const tasksLoading = ref(false);
const teamLoading = ref(false);
const userLoading = ref(false);
const workTypeLoading = ref(false);
const companyLoading = ref(false);

const team = ref<string>("");
const user = ref<string>("");
const status = ref<string>("");
const problem = ref<string>("");
const search = ref<string>("");
const isAdmin = ref<boolean>(false);

const isModalOpen = ref<boolean>(false);
const modalType = ref<"info" | "unarchive" | "delete">("info");
const modalItem = ref<ITaskData | null>(null);
const modalReason = ref<string>("");

let searchTimer: ReturnType<typeof setTimeout> | null = null;

let searchFilterProblemTimer: ReturnType<typeof setTimeout> | null = null;
let searchFilterUserTimer: ReturnType<typeof setTimeout> | null = null;

const collapsedDates = ref<Record<string, boolean>>({});
const collapsedShifts = ref<Record<string, boolean>>({});

const PAGE_SIZE = 50;
const page = ref(1);
const hasMore = ref(true);
const isLoadingMore = ref(false);
const scrollContainer = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.NEW]: "--color-system-blue",
  [TaskStatus.INFORM]: "--color-system-grey",
  [TaskStatus.IN_PROGRESS]: "--color-system-orange",
  [TaskStatus.ON_HOLD]: "--color-system-yellow",
  [TaskStatus.DONE]: "--color-system-green",
  [TaskStatus.QA_FAILED]: "--color-system-red",
  [TaskStatus.CLOSED]: "--color-system-green",
};

const problemFilterItems = computed(() =>
  (workTypes.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const userFilterItems = computed(() =>
  (users.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
  }))
);

const filteredHeaders = computed(() => {
  if (archiveStore.commonFilter.team_id) {
    return [
      {
        name: "id",
        label: "No",
        width: "3%",
      },
      {
        name: "company",
        label: "Company",
        width: "14%",
      },
      {
        name: "drivers",
        label: "Drivers",
        width: "14%",
      },
      {
        name: "users",
        label: "User",
        width: "13%",
      },
      {
        name: "date",
        label: "Start time",
        width: "6%",
      },
      {
        name: "completed_time",
        label: "End time",
        width: "6%",
      },
      {
        name: "points_awarded",
        label: "KPI",
        width: "3%",
      },
      {
        name: "status",
        label: "Status",
        width: "9%",
      },
      {
        name: "problems",
        label: "Problems",
        width: "24%",
      },
      {
        name: "actions",
        label: "",
        width: "8%",
      },
    ];
  } else {
    return [
      {
        name: "id",
        label: "No",
        width: "3%",
      },
      {
        name: "company",
        label: "Company",
        width: "14%",
      },
      {
        name: "drivers",
        label: "Drivers",
        width: "14%",
      },
      {
        name: "team",
        label: "Team",
        width: "5%",
      },
      {
        name: "users",
        label: "User",
        width: "13%",
      },
      {
        name: "date",
        label: "Start time",
        width: "6%",
      },
      {
        name: "completed_time",
        label: "End time",
        width: "6%",
      },
      {
        name: "points_awarded",
        label: "KPI",
        width: "3%",
      },
      {
        name: "status",
        label: "Status",
        width: "9%",
      },
      {
        name: "problems",
        label: "Problems",
        width: "19%",
      },
      {
        name: "actions",
        label: "",
        width: "8%",
      },
    ];
  }
});

const formatStatusLabel = (key: string) =>
  key.replace(/_/g, "_").replace(/\b\w/g, (c) => c.toUpperCase());

const statusItems = (
  Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>
).map((key) => ({
  value: TaskStatus[key],
  name: formatStatusLabel(key),
  color: STATUS_COLORS[TaskStatus[key]],
}));

const hasUserHasAction = (item: ITaskData) => {
  return (
    usersStore.userProfile &&
    (usersStore.userProfile.id == item.assignee_id ||
      usersStore.userProfile.role == "admin" ||
      usersStore.userProfile.role == "supervisor")
  );
};

const getStatus = (qaStatus: string) => {
  const key = (Object.keys(TaskStatus) as Array<keyof typeof TaskStatus>).find(
    (k) => TaskStatus[k] === qaStatus
  );
  return key ? formatStatusLabel(key) : null;
};

const toggleDate = (date: string) => {
  collapsedDates.value[date] = !collapsedDates.value[date];
};

const toggleShift = (date: string, shift: string) => {
  const key = `${date}::${shift}`;
  collapsedShifts.value[key] = !collapsedShifts.value[key];
};

const isDateCollapsed = (date: string) => collapsedDates.value[date];
const isShiftCollapsed = (date: string, shift: string) =>
  collapsedShifts.value[`${date}::${shift}`];

const groupedByDate = computed(() => {
  const dateMap = new Map<string, Map<string, ITaskData[]>>();

  for (const task of tasks.value) {
    const date = task.created_time
      ? DateTime.fromISO(task.created_time, { zone: "utc" })
          .setZone("America/New_York")
          .toFormat("yyyy-MM-dd")
      : "Unknown";

    const shift = task.shift_name ?? "Unknown Shift";

    if (!dateMap.has(date)) {
      dateMap.set(date, new Map());
    }

    const shiftMap = dateMap.get(date)!;

    if (!shiftMap.has(shift)) {
      shiftMap.set(shift, []);
    }

    shiftMap.get(shift)!.push(task);
  }

  return Array.from(dateMap.entries()).map(([date, shiftMap]) => {
    const shiftGroups = Array.from(shiftMap.entries()).map(
      ([label, items]) => ({
        label,
        items,
      })
    );

    return { date, shiftGroups };
  });
});

function datepickerToUtcDateOnly(d: string | Date | null | undefined) {
  if (!d) return "";

  const date =
    typeof d === "string"
      ? DateTime.fromISO(d, { zone: "utc" })
      : DateTime.fromJSDate(d, { zone: "utc" });

  return date.setZone("America/New_York").toFormat("HH:mm:ss");
}

// --- pagination helpers ------------------------------------------------------
const fetchArchives = async (opts: { append: boolean }) => {
  archiveStore.commonFilter.page = page.value;
  archiveStore.commonFilter.per_page = PAGE_SIZE;

  const loadingRef = opts.append ? isLoadingMore : tasksLoading;

  await archiveStore.getArchives(loadingRef, (data: any) => {
    if (!data) return;

    const items: ITaskData[] = data.items ?? [];

    tasks.value = opts.append ? [...tasks.value, ...items] : items;

    hasMore.value =
      data.has_more !== undefined ? data.has_more : items.length === PAGE_SIZE;
  });
};

const resetAndFetch = async () => {
  page.value = 1;
  hasMore.value = true;
  await fetchArchives({ append: false });
};

const loadMore = async () => {
  if (tasksLoading.value || isLoadingMore.value || !hasMore.value) return;
  page.value += 1;
  await fetchArchives({ append: true });
};
// -------------------------------------------------------------------------------

const getList = async () => {
  const [fetchedTeams, fetchedWorkTypes, fetchedCompanies, fetchUsers] =
    await Promise.all([
      teamStore.getTeams(teamLoading),
      workTypeStore.getWorkTypes(workTypeLoading),
      companyStore.getCompanies(companyLoading),
      usersStore.getUsers(userLoading),
    ]);

  await resetAndFetch();

  teams.value = fetchedTeams;
  workTypes.value = fetchedWorkTypes;
  companies.value = fetchedCompanies.items;
  problems.value = fetchedWorkTypes;
  users.value = fetchUsers;
};

const formatToEdt = (date: any) => {
  if (!date) return "";

  return date.split("T")[1] == "11:00:00.000Z"
    ? DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d")
    : DateTime.fromISO(date, { zone: "utc" })
        .setZone("America/New_York")
        .toFormat("LLL d, HH:mm ZZZZ");
};

const tooltipContent = (list: any[]) =>
  list
    ?.map((item) => `<p>${item.label} - ${formatToEdt(item.date)}</p>`)
    ?.join("");

const openModal = (type: "info" | "unarchive" | "delete", item: ITaskData) => {
  modalType.value = type;
  modalItem.value = item;
  isModalOpen.value = true;
  modalReason.value = "";
};

const closeModal = () => {
  modalType.value = "info";
  modalItem.value = null;
  isModalOpen.value = false;
  modalReason.value = "";
};

const saveModal = async () => {
  if (modalType.value === "unarchive") {
    if (!modalItem.value) return;
    await archiveStore.unarchiveTask(
      tasksLoading,
      modalItem.value?.id,
      modalReason.value,
      async (data: any) => {
        if (data) {
          pushNotification("Successfully unarchived", "success");
          closeModal();
          await getList();
        }
      }
    );
  } else if (modalType.value === "delete") {
    if (!modalItem.value) return;
    await archiveStore.deleteTask(
      tasksLoading,
      modalItem.value.id,
      async (data: any) => {
        if (data) {
          pushNotification("Successfully deleted", "success");
          closeModal();
          await getList();
        }
      }
    );
  }
};

const searchFilterWorkType = (val: string) => {
  if (searchFilterProblemTimer) clearTimeout(searchFilterProblemTimer);
  searchFilterProblemTimer = setTimeout(async () => {
    workTypeStore.commonFilter.search = val;
    const [fetchedWorkTypes] = await Promise.all([
      workTypeStore.getWorkTypes(workTypeLoading),
    ]);

    workTypes.value = fetchedWorkTypes;
  }, 400);
};

const searchFilterUser = (val: string) => {
  if (searchFilterUserTimer) clearTimeout(searchFilterUserTimer);
  searchFilterUserTimer = setTimeout(async () => {
    usersStore.commonFilter.q = val;
    const [fetchedUsers] = await Promise.all([
      usersStore.getUsers(userLoading),
    ]);

    users.value = fetchedUsers;
  });
};

onMounted(async () => {
  companyStore.commonFilter.search = "";
  driverStore.commonFilter.search = "";
  workTypeStore.commonFilter.search = "";

  if (usersStore.userProfile?.role === "admin") {
    isAdmin.value = true;
  }

  if (archiveStore.commonFilter.search) {
    search.value = archiveStore.commonFilter.search;
  }

  if (archiveStore.commonFilter.team_id) {
    team.value = archiveStore.commonFilter.team_id;
  } else {
    if (usersStore.userProfile && usersStore.userProfile.team_id) {
      team.value = usersStore.userProfile.team_id;
      companyStore.commonFilter.team_id = usersStore.userProfile.team_id;
      archiveStore.commonFilter.team_id = usersStore.userProfile.team_id;
    }
  }

  if (archiveStore.commonFilter.status) {
    status.value = archiveStore.commonFilter.status;
  }

  if (archiveStore.commonFilter.work_type_id) {
    problem.value = archiveStore.commonFilter.work_type_id;
  }
  await getList();

  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    },
    { root: scrollContainer.value, rootMargin: "200px" }
  );
  if (sentinel.value) observer.observe(sentinel.value);
});

watch(
  () => search.value,
  async (val) => {
    if (tasksLoading.value || isLoadingMore.value) return;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(async () => {
      archiveStore.commonFilter.search = val;
      await resetAndFetch();
    }, 400);
  },
  {
    immediate: false,
  }
);

watch(
  () => user.value,
  async (val: string) => {
    if (tasksLoading.value || isLoadingMore.value) return;
    archiveStore.commonFilter.assignee_id = val;
    await resetAndFetch();
  },
  { immediate: false }
);

watch(
  () => team.value,
  async (val: string) => {
    if (tasksLoading.value || isLoadingMore.value) return;
    archiveStore.commonFilter.team_id = val;
    await resetAndFetch();
  },
  { immediate: false }
);

watch(
  () => status.value,
  async (val: string) => {
    if (tasksLoading.value || isLoadingMore.value) return;
    archiveStore.commonFilter.status = val;
    await resetAndFetch();
  },
  { immediate: false }
);

watch(
  () => problem.value,
  async (val: string) => {
    if (tasksLoading.value || isLoadingMore.value) return;
    archiveStore.commonFilter.work_type_id = val;
    await resetAndFetch();
  },
  { immediate: false }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);

onUnmounted(() => {
  observer?.disconnect();
  archiveStore.clearFilter();
  workTypeStore.clearFilter();
  usersStore.clearFilter();
});
</script>

<template>
  <div class="v-tasks" ref="scrollContainer">
    <div class="v-tasks__header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        :placeholder="'Search'"
        clearable
      />
      <CommonDropdown
        v-model="team"
        :items="teams.map((t: any) => ({ value: t.id, name: t.name }))"
        label="Team"
        position="bottom"
        dropdownFullWidth
        clearable
      />
      <CustomSearchDropdown
        v-model="user"
        :items="userFilterItems"
        label="User"
        position="bottom"
        clearable
        dropdownFullWidth
        maxWidth="200px"
        @search="searchFilterUser"
      />
      <CommonDropdown
        v-model="status"
        :items="statusItems"
        label="Status"
        position="bottom"
        clearable
      />
      <CustomSearchDropdown
        v-model="problem"
        :items="problemFilterItems"
        label="Problem"
        position="bottom"
        clearable
        dropdownFullWidth
        maxWidth="400px"
        @search="searchFilterWorkType"
      />
    </div>

    <div class="v-tasks__body">
      <template
        v-for="(dateGroup, dateIndex) in groupedByDate"
        :key="dateIndex"
      >
        <div
          class="v-tasks__date-row"
          @click="toggleDate(dateGroup.date)"
          :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
        >
          <span class="v-tasks__date-label">{{ dateGroup.date }}</span>
          <span
            class="v-tasks__date-chevron"
            :class="{ 'is-collapsed': isDateCollapsed(dateGroup.date) }"
          >
            <ChevronDown :size="20" />
          </span>
        </div>

        <template v-if="!isDateCollapsed(dateGroup.date)">
          <template
            v-for="(shiftGroup, shiftIndex) in dateGroup.shiftGroups"
            :key="shiftIndex"
          >
            <CommonTable
              :headers="filteredHeaders"
              :groups="[{ label: shiftGroup.label, items: shiftGroup.items }]"
              :loading="tasksLoading"
              :collapsed="isShiftCollapsed(dateGroup.date, shiftGroup.label)"
              @toggle-group="toggleShift(dateGroup.date, shiftGroup.label)"
              isTaskTable
              :dateIndex="dateIndex"
              :shiftIndex="shiftIndex"
              :isAdmin="isAdmin"
              :withoutHeader="shiftIndex > 0"
            >
              <template #id="{ index }">
                <span>{{ index + 1 }}</span>
              </template>

              <template #company="{ item }">
                <div class="company__name">
                  <span>{{ item.company_name }}</span>
                  <span class="app">{{
                    item.provider_name?.split(" ")[0] || "Hos"
                  }}</span>
                </div>
              </template>

              <template #drivers="{ item }">
                <span class="v-users__badge">{{ item.driver_name }}</span>
              </template>

              <template #team="{ item }">
                <span class="v-users__badge">{{ item.team_name }}</span>
              </template>

              <template #users="{ item }">
                <span>{{ item.assignee_name }}</span>
              </template>

              <template #date="{ item }">
                <span>{{ datepickerToUtcDateOnly(item.created_time) }}</span>
              </template>

              <template #completed_time="{ item }">
                <span>{{
                  datepickerToUtcDateOnly(item.completed_time) || "-"
                }}</span>
              </template>

              <template #status="{ item }">
                <CommonStatus
                  v-if="item.status && getStatus(item.status)"
                  :status="getStatus(item.status)!"
                  :showActions="false"
                  :tooltipContent="
                    tooltipContent(item.qa_failed_reason_details)
                  "
                />
                <span v-else>—</span>
              </template>

              <template #problems="{ item }">
                <span>{{ item.description || item.work_type_name }}</span>
              </template>

              <template #actions="{ item }">
                <div class="action">
                  <div class="icon" @click="openModal('info', item)">
                    <Eye :size="14" />
                  </div>
                  <div class="icon" v-if="hasUserHasAction">
                    <ReverseLeft
                      :size="14"
                      @click="openModal('unarchive', item)"
                    />
                  </div>
                  <div
                    v-if="
                      usersStore.userProfile?.role === 'super_admin' ||
                      usersStore.userProfile?.role == 'admin' ||
                      usersStore.userProfile?.role == 'supervisor'
                    "
                    class="icon"
                    @click="openModal('delete', item)"
                  >
                    <Trash01 :size="14" />
                  </div>
                </div>
              </template>
            </CommonTable>
          </template>
        </template>
      </template>

      <div ref="sentinel" class="v-tasks__sentinel"></div>

      <div v-if="isLoadingMore" class="v-tasks__loading-more">
        Loading more…
      </div>
      <div
        v-else-if="!hasMore && tasks.length && !tasksLoading"
        class="v-tasks__end"
      >
        No more tasks
      </div>
    </div>

    <CommonModal
      v-model="isModalOpen"
      :title="
        modalType == 'info'
          ? 'Archive reason'
          : modalType == 'delete'
          ? 'Delete'
          : 'Unarchive'
      "
    >
      <div v-if="modalType == 'unarchive'" class="new-modal info">
        <CommonInput v-model="modalReason" label="Reason" multiline />
      </div>

      <div class="info" v-else-if="modalType == 'delete'">
        This will permanently delete this task. You cannot undo this action.
      </div>

      <div class="info" v-else>
        {{ modalItem?.archive_reason || "-" }}
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            :label="modalType == 'info' ? 'Close' : 'Cancel'"
            @clicked="closeModal"
          />
          <CommonButton
            v-if="modalType !== 'info'"
            type="primary"
            :label="modalType == 'delete' ? 'Delete' : 'Save'"
            @clicked="saveModal"
          />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-tasks {
  padding: var(--base-space-16);
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  overflow: auto;

  &__header {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: var(--base-space-8);
    margin-bottom: var(--base-space-16);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--base-space-4) var(--base-space-8);
    gap: var(--base-space-8);
    border-radius: var(--base-radius-8);
    color: var(--color-label);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    &.active {
      background-color: var(--color-green-light);
    }

    &.inactive {
      background-color: var(--color-red-light);
    }
  }

  &__actions {
    display: flex;
    gap: var(--base-space-8);
  }

  &__btn {
    cursor: pointer;
    transition: opacity 0.2s;
    background-color: var(--color-bg-secondary);
    color: var(--color-label);
    border-color: var(--color-border);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--base-space-8);
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--base-radius-12);

    &:hover {
      opacity: 0.8;
    }
  }

  &__date-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--base-space-8);
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
    border-top-left-radius: var(--base-radius-16);
    border-top-right-radius: var(--base-radius-16);
    transition: background-color 0.15s;
    background: var(--color-secondary-system-background);
    margin-top: 10px;
    height: 40px;

    &.is-collapsed {
      border-radius: var(--base-radius-16);
    }
  }

  &__date-chevron {
    display: flex;
    align-items: center;
    color: var(--color-label-secondary);
    transition: transform 0.2s;

    &.is-collapsed {
      transform: rotate(-180deg);
    }
  }

  &__date-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-label);
  }

  &__sentinel {
    height: 1px;
  }

  &__loading-more,
  &__end {
    text-align: center;
    padding: var(--base-space-16);
    font-size: 13px;
    color: var(--color-label-secondary);
  }

  :deep(.c-co-table) {
    margin-top: 0 !important;
  }

  :deep(.table thead th) {
    &:first-child {
      border-top-left-radius: 0 !important;
    }

    &:last-child {
      border-top-right-radius: 0 !important;
    }
  }

  .td__dropdown {
    padding: 0;

    .problem__group {
      display: flex;

      &--save,
      &--edit {
        margin: 8px 12px;
        background: var(--color-secondary-system-background);
        border-radius: var(--base-radius-8);
        padding: 4px 8px;
        color: var(--color-primary);
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;

        &.disabled {
          cursor: not-allowed;
        }
      }

      &--edit {
        margin: 8px 0 8px 12px;
      }
    }
  }

  .custom__problem {
    display: flex;
    align-items: center;

    .problem__save {
      background: var(--color-secondary-system-background);
      border-radius: var(--base-radius-8);
      padding: 4px 8px;
      color: var(--color-primary);
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;

      &.disabled {
        cursor: not-allowed;
      }
    }
  }
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}

.company__name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;

  .app {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: var(--color-label-secondary);
    white-space: nowrap;
  }
}

.action {
  display: flex;
  align-items: center;
  gap: var(--base-space-8);

  .icon {
    background: var(--color-secondary-system-background);
    border-radius: var(--base-radius-8);
    height: 28px;
    width: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--color-primary);
  }
}

.info {
  color: var(--color-label);
}
</style>
